/** @file Session action: run one analysis (never committed), preview its plan and show the ghost. */
import { analysisArgs } from './analysisArgs';
import type { AnalysisReply, AnalysisRequest, AutorigPorts, AutorigView, PlanPreview, ReviewView, SessionCore } from './autorigTypes';
import { ghostOf } from './ghostOf';
import { previewPlan } from './previewPlan';

/** A commit or a revert in flight, or an analysis already running: a new run waits for it. */
const busy = (view: AutorigView): boolean =>
  view.kind === 'running' || (view.kind === 'review' && view.applying) || (view.kind === 'applied' && view.undoing);

const NO_DOC: PlanPreview = { kind: 'failed', thrown: new Error('no document is open') };

/** The ghost is built only on the document the plan was made on: confirmed rev === analysed rev (W3-71). */
function previewOf(ports: AutorigPorts, reply: AnalysisReply): PlanPreview {
  const doc = ports.currentDoc();
  const docRev = ports.confirmedRev();
  if (doc === null || docRev === null) return NO_DOC;
  if (reply.plan.commands.length > 0 && docRev !== reply.rev) return { kind: 'outOfSync', planRev: reply.rev, docRev };
  return previewPlan(doc, reply.plan, ports.now());
}

/** Ignored while busy; a close or a newer run drops this run's answer (ticket). */
export async function runAnalysis(core: SessionCore, request: AnalysisRequest): Promise<void> {
  if (busy(core.store.getState())) return;
  core.ticket.n += 1;
  const ticket = core.ticket.n;
  core.ports.showGhost(null);
  core.store.setState({ kind: 'running', request }, true);
  try {
    const reply = await core.ports.analyse(request.fn, analysisArgs(request));
    if (ticket !== core.ticket.n) return;
    const view: ReviewView = { kind: 'review', request, reply, preview: previewOf(core.ports, reply), ghost: true, applying: false, failure: null };
    core.store.setState(view, true);
    core.ports.showGhost(ghostOf(view));
  } catch (thrown) {
    if (ticket === core.ticket.n) core.store.setState({ kind: 'failed', request, thrown }, true);
  }
}
